/**
 * Canvas scatter via uPlot when SimpleScatter has too many points for SVG circles.
 */
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { debug, error as logError } from "./console";
import { myTickFormat } from "./global";
import { prepareSimpleScatterUPlotSeries } from "./simpleScatterUPlotGate";

export {
  SIMPLE_SCATTER_UPLOT_MIN_POINTS,
  shouldUseSimpleScatterUPlot,
  prepareSimpleScatterUPlotSeries,
} from "./simpleScatterUPlotGate";

export interface SimpleScatterUPlotPoint {
  x: number;
  y: number;
  ID?: number;
  color: string;
  opacity: number;
  visible: boolean;
}

export interface SimpleScatterUPlotMount {
  instance: uPlot;
  overlay: SVGSVGElement;
  root: HTMLDivElement;
  setPoints: (points: SimpleScatterUPlotPoint[]) => void;
  redraw: () => void;
  destroy: () => void;
}

export interface MountSimpleScatterUPlotSpec {
  width: number;
  height: number;
  xLabel: string;
  yLabel: string;
  axisColor: string;
  gridColor: string;
  points: SimpleScatterUPlotPoint[];
  /** Match D3 SimpleScatter `xymargin`: [top, right, bottom, left]. */
  padding: [number, number, number, number];
}

const POINT_RADIUS_CSS = 4;

export function mountSimpleScatterUPlot(
  host: HTMLElement,
  spec: MountSimpleScatterUPlotSpec
): SimpleScatterUPlotMount {
  host.querySelectorAll(
    ".simple-scatter-uplot-root, .simple-scatter-uplot-overlay, .simple-scatter-uplot-labels"
  ).forEach((n) => n.remove());

  const root = document.createElement("div");
  root.className = "simple-scatter-uplot-root";
  host.appendChild(root);

  const overlay = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  overlay.setAttribute("class", "simple-scatter-uplot-overlay");
  const hit = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  hit.setAttribute("class", "simple-scatter-uplot-lasso-hit");
  hit.setAttribute("x", "0");
  hit.setAttribute("y", "0");
  hit.setAttribute("width", "100%");
  hit.setAttribute("height", "100%");
  hit.setAttribute("fill", "transparent");
  overlay.appendChild(hit);
  host.appendChild(overlay);

  let points = spec.points;

  const plotWidth = Math.max(80, Math.floor(spec.width));
  const plotHeight = Math.max(80, Math.floor(spec.height));
  const tickFontCss = Math.min(13, Math.max(10, Math.round(Math.min(plotWidth, plotHeight) * 0.028)));
  const tickFont = `${tickFontCss}px sans-serif`;
  const prepared = prepareSimpleScatterUPlotSeries(points);
  let xMin = prepared.xMin;
  let xMax = prepared.xMax;
  let yMin = prepared.yMin;
  let yMax = prepared.yMax;

  const opts: uPlot.Options = {
    width: plotWidth,
    height: plotHeight,
    legend: { show: false },
    cursor: { show: false, drag: { x: false, y: false, setScale: false } },
    select: { show: false },
    padding: spec.padding,
    scales: {
      x: {
        time: false,
        distr: 1,
        range: () => [xMin, xMax],
      },
      y: {
        time: false,
        distr: 1,
        range: () => [yMin, yMax],
      },
    },
    axes: [
      {
        stroke: spec.axisColor,
        grid: { stroke: spec.gridColor, width: 1 },
        ticks: { show: true, stroke: spec.axisColor, size: Math.round(tickFontCss * 0.45) },
        label: "",
        labelSize: 0,
        gap: Math.round(tickFontCss * 0.35),
        size: Math.round(tickFontCss * 2.1),
        values: (_u, splits) => splits.map((v) => myTickFormat(v)),
        font: tickFont,
      },
      {
        stroke: spec.axisColor,
        grid: { stroke: spec.gridColor, width: 1 },
        ticks: { show: true, stroke: spec.axisColor, size: Math.round(tickFontCss * 0.45) },
        label: "",
        labelSize: 0,
        gap: Math.round(tickFontCss * 0.45),
        size: Math.round(tickFontCss * 4.2),
        values: (_u, splits) => splits.map((v) => myTickFormat(v)),
        font: tickFont,
      },
    ],
    series: [
      {},
      {
        // Draw in paths() so uPlot includes the cloud in the series pass.
        // hooks.draw alone was empty in production (clip / pass order).
        paths: (u) => {
          drawScatterPoints(u, points);
          return null;
        },
        points: { show: false },
      },
    ],
    hooks: {
      draw: [
        (u) => {
          drawScatterPoints(u, points);
        },
      ],
      setSize: [
        (u) => {
          syncOverlay(u, overlay);
        },
      ],
    },
  };

  let instance: uPlot;
  try {
    instance = new uPlot(opts, [prepared.xs, prepared.ys], root);
  } catch (err) {
    logError("SimpleScatter uPlot create failed", err);
    throw err;
  }
  syncOverlay(instance, overlay);
  appendD3StyleAxisLabels(host, spec);

  const setPoints = (next: SimpleScatterUPlotPoint[]) => {
    points = next;
    const nextPrepared = prepareSimpleScatterUPlotSeries(next);
    xMin = nextPrepared.xMin;
    xMax = nextPrepared.xMax;
    yMin = nextPrepared.yMin;
    yMax = nextPrepared.yMax;
    instance.setData([nextPrepared.xs, nextPrepared.ys]);
    instance.setScale("x", { min: xMin, max: xMax });
    instance.setScale("y", { min: yMin, max: yMax });
    syncOverlay(instance, overlay);
  };

  return {
    instance,
    overlay,
    root,
    setPoints,
    redraw: () => instance.redraw(),
    destroy: () => {
      try {
        instance.destroy();
      } catch {
        debug("SimpleScatter uPlot destroy skipped");
      }
      root.remove();
      overlay.remove();
      host.querySelector(".simple-scatter-uplot-labels")?.remove();
    },
  };
}

function plotPxRatio(u: uPlot): number {
  const r = u.pxRatio;
  return typeof r === "number" && Number.isFinite(r) && r > 0
    ? r
    : (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1);
}

function syncOverlay(u: uPlot, overlay: SVGSVGElement): void {
  const px = plotPxRatio(u);
  const left = u.bbox.left / px;
  const top = u.bbox.top / px;
  const width = Math.max(u.bbox.width / px, 0);
  const height = Math.max(u.bbox.height / px, 0);
  overlay.setAttribute("viewBox", `0 0 ${width} ${height}`);
  overlay.setAttribute("width", String(width));
  overlay.setAttribute("height", String(height));
  overlay.style.left = `${left}px`;
  overlay.style.top = `${top}px`;
  overlay.style.width = `${width}px`;
  overlay.style.height = `${height}px`;
  const hit = overlay.querySelector(".simple-scatter-uplot-lasso-hit");
  if (hit) {
    hit.setAttribute("width", String(width));
    hit.setAttribute("height", String(height));
  }
}

function drawScatterPoints(u: uPlot, points: SimpleScatterUPlotPoint[]): void {
  const ctx = u.ctx;
  if (!ctx) {
    return;
  }
  const ratio = u.pxRatio > 0 ? u.pxRatio : (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1);
  const r = Math.max(1.2, POINT_RADIUS_CSS * ratio);
  ctx.save();
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    if (!p.visible) {
      continue;
    }
    const sx = u.valToPos(p.x, "x", true);
    const sy = u.valToPos(p.y, "y", true);
    if (!Number.isFinite(sx) || !Number.isFinite(sy)) {
      continue;
    }
    const alpha = p.opacity > 0 ? p.opacity : 0.6;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = p.color || "#1f77b4";
    ctx.beginPath();
    ctx.arc(sx, sy, r, 0, Math.PI * 2);
    ctx.fill();
    // Dimmed points skip the white ring — overlapping strokes read as a bright haze.
    if (alpha >= 0.4) {
      ctx.strokeStyle = "white";
      ctx.lineWidth = Math.max(1, ratio);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function appendD3StyleAxisLabels(host: HTMLElement, spec: MountSimpleScatterUPlotSpec): void {
  const [, padRight, padBottom, padLeft] = spec.padding;
  const plotW = Math.max(0, spec.width - padLeft - padRight);
  const plotH = Math.max(0, spec.height - spec.padding[0] - padBottom);
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "simple-scatter-uplot-labels");
  svg.setAttribute("width", String(spec.width));
  svg.setAttribute("height", String(spec.height));

  const yText = document.createElementNS("http://www.w3.org/2000/svg", "text");
  yText.setAttribute("class", "y-label chart-element y-label--start simple-scatter-y-label");
  yText.setAttribute("text-anchor", "start");
  yText.setAttribute("x", String(padLeft + plotW * 0.12));
  yText.setAttribute("y", String(spec.padding[0] * 0.55));
  yText.textContent = spec.yLabel || "Y-Axis";

  const xText = document.createElementNS("http://www.w3.org/2000/svg", "text");
  xText.setAttribute("class", "x-label chart-element");
  xText.setAttribute("text-anchor", "middle");
  xText.setAttribute("font-size", "20px");
  xText.setAttribute(
    "transform",
    `translate(${padLeft + plotW / 2}, ${spec.padding[0] + plotH + padBottom * 0.02})`
  );
  xText.textContent = spec.xLabel || "X-Axis";

  svg.appendChild(yText);
  svg.appendChild(xText);
  host.appendChild(svg);
}
